<script lang="ts">
  import {
    barHeight,
    barReadout,
    developsNote,
    isDryHour,
    modelDevelops,
    motionLine,
    nextHourBars,
    nextHourSentence,
  } from '../../forecast/nexthour'
  import { radarLine } from '../../forecast/now'
  import { radar, radarHere } from '../../state/now.svelte'

  /**
   * "Next hour" (nowcast plan, Stage B): the sentence, then thirty bars, one
   * per two minutes, on one *intensity* scale with light, moderate and heavy
   * guides. Rain and snow share the scale, not a unit, so a bar's colour is
   * its type and its height is its place in that type's own bands. A dry hour
   * is the sentence alone (owner's decision, plan §8).
   *
   * Words on the sentence and the guides; the rate in in/h only when a bar is
   * tapped (plan §8). Untapped, the line under the bars names the frame and
   * how the echoes are moving: the hour is the radar slid along its own
   * motion, which cannot start a storm that does not exist yet (plan §6),
   * and saying what it is made of is the honest way to say so.
   *
   * Stage D's cross-check: when that hour is dry and HRRR has rain forming
   * nearby, the sentence says "moving in" and one muted line under it says
   * why. Never bars: the model is a caveat on the radar, not a forecast of
   * its own minutes.
   */

  let { timezone }: { timezone: string | null } = $props()

  const uid = $props.id()

  let reading = $derived(radarHere())
  let bars = $derived(nextHourBars(reading, radar.clock))
  let develops = $derived(modelDevelops(reading?.model, radar.clock))
  let sentence = $derived(bars ? nextHourSentence(bars, radar.clock, false, develops) : '')
  let dry = $derived(bars ? isDryHour(bars) : true)

  /** The tapped bar, by its start time, so a refresh keeps it if it survives. */
  let selectedAt = $state<number | null>(null)
  let selected = $derived(bars?.find((b) => b.at === selectedAt) ?? null)

  let footnote = $derived.by(() => {
    if (!reading) return ''
    const moving = motionLine(reading.motion)
    return [radarLine({ ...reading, rate_in_h: null }, timezone ?? undefined), moving]
      .filter(Boolean)
      .join(' · ')
  })

  let track: HTMLElement | undefined = $state()
  let scrubbing = false

  function pick(e: PointerEvent) {
    if (!track || !bars) return
    const box = track.getBoundingClientRect()
    const i = Math.floor(((e.clientX - box.left) / box.width) * bars.length)
    const bar = bars[Math.min(Math.max(i, 0), bars.length - 1)]
    selectedAt = bar.at
  }
</script>

{#if bars}
  <section class="next-hour" aria-labelledby="{uid}-title">
    <h2 id="{uid}-title" class="wx-sec-head section-title">Next hour</h2>
    <p class="sentence">{sentence}</p>
    {#if dry && develops && reading?.model}
      <p class="caveat">{developsNote(reading.model)}</p>
    {/if}
    {#if !dry}
      <div class="chart">
        <!-- The guides are the scale's words, top down; the bars are on the
             same three equal steps (`mrms.level`). -->
        <ol class="guides" aria-hidden="true">
          <li>Heavy</li>
          <li>Moderate</li>
          <li>Light</li>
        </ol>
        <div
          class="track"
          role="group"
          aria-label="Precipitation every two minutes"
          bind:this={track}
          onpointerdown={(e) => {
            scrubbing = true
            ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
            pick(e)
          }}
          onpointermove={(e) => scrubbing && pick(e)}
          onpointerup={() => (scrubbing = false)}
          onpointercancel={() => (scrubbing = false)}
        >
          {#each bars as bar (bar.at)}
            <button
              type="button"
              class="bar"
              class:snow={bar.type === 'snow'}
              class:unknown={!bar.coverage}
              class:selected={bar.at === selectedAt}
              aria-label={barReadout(bar, timezone ?? undefined)}
              aria-pressed={bar.at === selectedAt}
              onclick={() => (selectedAt = bar.at)}
            >
              <span class="fill" style:height="{barHeight(bar) * 100}%"></span>
            </button>
          {/each}
        </div>
        <ol class="ticks" aria-hidden="true">
          <li>Now</li>
          <li>15</li>
          <li>30</li>
          <li>45</li>
          <li>60 min</li>
        </ol>
      </div>
      <!-- Polite, so a scrub reads out where it lands without interrupting. -->
      <p class="readout" aria-live="polite">
        {selected ? barReadout(selected, timezone ?? undefined) : footnote}
      </p>
    {/if}
  </section>
{/if}

<style>
  /* Phase 11's section head, set in from the gutter so its rule is the
     content's width. */
  .section-title {
    margin: 30px var(--gutter, 0px) 0;
  }

  p {
    margin: 0;
    padding: 0 var(--gutter, 0px);
  }

  .sentence {
    margin: 12px 0 14px;
    font-size: 19px;
    font-weight: 600;
    line-height: 1.25;
  }

  /* Under a dry sentence HRRR has qualified; the readout's type, so it reads
     as a note on the sentence rather than a second one. */
  .caveat {
    margin-top: -8px;
    padding-bottom: 14px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-muted);
  }

  .chart {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    grid-template-rows: 90px auto;
    margin: 0 var(--gutter, 0px);
  }

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .guides {
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    font-size: 13px;
    color: var(--color-muted);
  }

  .guides li {
    display: flex;
    align-items: flex-end;
    padding-bottom: 2px;
  }

  .track {
    position: relative;
    display: flex;
    align-items: stretch;
    gap: 2px;
    /* A finger scrubs the bars sideways; scrolling the panel stays vertical. */
    touch-action: pan-y;
    /* The guides, drawn behind the bars: the scale's two inner lines and its
       base, at thirds. */
    background:
      linear-gradient(var(--color-divider), var(--color-divider)) 0 33.333% / 100% 1px no-repeat,
      linear-gradient(var(--color-divider), var(--color-divider)) 0 66.667% / 100% 1px no-repeat;
    border-bottom: 2px solid var(--color-text);
  }

  .bar {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: flex-end;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .bar:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 1px;
  }

  .fill {
    width: 100%;
    background: var(--color-rain);
  }

  .snow .fill {
    background: var(--color-snow);
  }

  /* Beyond the radars: not a dry bar, a missing one. */
  .unknown {
    background: repeating-linear-gradient(
      -45deg,
      transparent 0 3px,
      color-mix(in srgb, var(--color-text) 14%, transparent) 3px 4px
    );
  }

  .selected {
    box-shadow: inset 0 0 0 1px var(--color-text);
  }

  .ticks {
    grid-column: 2;
    display: flex;
    justify-content: space-between;
    padding-top: 6px;
    font-size: 13px;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
  }

  .readout {
    min-height: 1.4em;
    padding-top: 10px;
    font-size: 13px;
    color: var(--color-muted);
    font-variant-numeric: tabular-nums;
  }
</style>
